/**
 * Chatbot Questionnaire Logic
 * Handles question flow, branching logic, and questionnaire completion
 */

function initChatbotQuestionnaire() {
  'use strict';

  // State management
  let questionsQueue = [];
  let questionsMap = {};
  let questionnaireAnswers = [];
  let currentQuestionIndex = 0;

  // Data and DOM elements (to be set by main chatbot file)
  let questionnaireContent;
  let progressFill;
  let currentQuestionSpan;
  let totalQuestionsSpan;
  let nextQuestionBtn;
  let prevQuestionBtn;
  let QUESTIONNAIRE_ENDPOINT;
  let CUSTOMER_ID;
  let CUSTOMER_NAME;
  let CUSTOMER_EMAIL;
  let questionsByOrderIndex = {};
  let questionnaireData = null;
  let showChatInterface;
  let messagesContainer;
  let maxTotalQuestions = 0; // track maximum possible questions for progress bar

  /**
   * Set DOM references after elements are created
   */
  function setDOMReferences(refs) {
    questionnaireContent = refs.questionnaireContent;
    progressFill = refs.progressFill;
    currentQuestionSpan = refs.currentQuestionSpan;
    totalQuestionsSpan = refs.totalQuestionsSpan;
    nextQuestionBtn = refs.nextQuestionBtn;
    prevQuestionBtn = refs.prevQuestionBtn;
    QUESTIONNAIRE_ENDPOINT = refs.QUESTIONNAIRE_ENDPOINT;
    CUSTOMER_ID = refs.CUSTOMER_ID;
    CUSTOMER_NAME = refs.CUSTOMER_NAME;
    CUSTOMER_EMAIL = refs.CUSTOMER_EMAIL;
    questionsByOrderIndex = refs.questionsByOrderIndex || {};
    questionnaireData = refs.questionnaireData;
    showChatInterface = refs.showChatInterface;
    messagesContainer = refs.messagesContainer;

    // Helper for completeQuestionnaire to access name/email
    this._customerName = CUSTOMER_NAME;
    this._customerEmail = CUSTOMER_EMAIL;

    bindEvents();
  }

  /**
   * Bind event listeners to DOM elements
   */
  function bindEvents() {
    if (nextQuestionBtn) {
      // Cleanly update listener without destroying the DOM node
      nextQuestionBtn.removeEventListener('click', nextQuestion);
      nextQuestionBtn.addEventListener('click', nextQuestion);
    }

    if (prevQuestionBtn) {
      prevQuestionBtn.removeEventListener('click', previousQuestion);
      prevQuestionBtn.addEventListener('click', previousQuestion);
    }
  }

  /**
   * Build initial questions queue from base_order
   */
  function buildQuestionsQueue() {
    // Extensive defensive checks for production stability
    if (!questionnaireData) {
      console.error("[Questionnaire] Cannot build queue: questionnaireData is null");
      return;
    }
    if (!questionnaireData.order || !Array.isArray(questionnaireData.order.base_order)) {
      console.error("[Questionnaire] Cannot build queue: Invalid order/base_order", questionnaireData.order);
      return;
    }

    const baseOrder = questionnaireData.order.base_order;
    questionsQueue = [];
    questionsMap = {};

    // Add base questions
    baseOrder.forEach(function (orderStr) {
      const orderNum = parseInt(orderStr);
      const question = questionsByOrderIndex[orderNum];
      if (question && question.question_id) {
        questionsQueue.push(question);
        questionsMap[question.question_id] = question;
      } else {
        console.warn("[Questionnaire] Missing or invalid question for orderNum:", orderNum);
      }
    });

    console.log("[Questionnaire] Queue built with " + questionsQueue.length + " base questions.");
  }

  /**
   * Get ladder questions based on answer to branching question
   */
  function getNextQuestionsFromLadder(question, selectedOptionId) {
    const ladderRules = questionnaireData.order.ladder_rules;
    const orderIndex = question.order_index.toString();

    if (!ladderRules[orderIndex]) return [];

    const rule = ladderRules[orderIndex][selectedOptionId];
    if (rule && Array.isArray(rule)) {
      const nextQuestions = [];
      rule.forEach(function (orderStr) {
        const orderNum = parseInt(orderStr);
        const nextQuestion = questionsByOrderIndex[orderNum];
        // Only add if not already in the map to avoid duplicates
        if (nextQuestion && !questionsMap[nextQuestion.question_id]) {
          nextQuestions.push(nextQuestion);
          questionsMap[nextQuestion.question_id] = nextQuestion;
        }
      });
      return nextQuestions;
    }
    return [];
  }

  /**
   * Initialize the questionnaire UI
   */
  function initializeQuestionnaire() {
    console.log("[Questionnaire] Initializing UI...");

    // 1. Build the logical queue
    buildQuestionsQueue();

    if (questionsQueue.length === 0) {
      console.error("[Questionnaire] Cannot initialize: empty questions queue.");
      if (questionnaireContent) {
        questionnaireContent.innerHTML = '<div style="text-align:center; padding: 20px; color:#ef4444;">No questions found in this set.</div>';
      }
      return;
    }

    // 2. Clear and Render Slides
    questionnaireContent.innerHTML = '';
    questionsQueue.forEach(function (question, index) {
      try {
        const slide = createQuestionSlide(question, index);
        questionnaireContent.appendChild(slide);
      } catch (err) {
        console.error("[Questionnaire] Failed to render slide index " + index, err, question);
      }
    });

    // Hard reset input UI state for a fresh run (important for retake flows).
    if (questionnaireContent) {
      const allInputs = questionnaireContent.querySelectorAll('input[type="radio"], input[type="checkbox"]');
      allInputs.forEach(function (input) {
        input.checked = false;
        input.disabled = false;
      });
    }

    // 3. Update UI Elements
    if (totalQuestionsSpan) {
      totalQuestionsSpan.textContent = questionsQueue.length;
    }

    // 4. Reset Index and Show First
    currentQuestionIndex = 0;
    showQuestion(0);

    console.log("[Questionnaire] UI initialization complete.");
  }

  /**
   * Calculate maximum possible total questions including all ladder branches
   */
  function calculateMaxTotalQuestions() {
    // Base questions count
    let total = questionsQueue.length;

    // Get all ladder questions from all branches
    if (!questionnaireData || !questionnaireData.order || !questionnaireData.order.ladder_rules) {
      return total;
    }

    const ladderRules = questionnaireData.order.ladder_rules;

    // Iterate through all rules to find potential max path
    Object.keys(ladderRules).forEach(function (orderIndex) {
      const rules = ladderRules[orderIndex];
      let maxBranch = 0;

      Object.keys(rules).forEach(function (optionId) {
        const ladderQuestions = rules[optionId];
        if (Array.isArray(ladderQuestions)) {
          maxBranch = Math.max(maxBranch, ladderQuestions.length);
        }
      });
      total += maxBranch;
    });

    return total;
  }

  /**
   * Create a question slide DOM element
   */
  function createQuestionSlide(question, index) {
    const div = document.createElement('div');
    div.className = 'question-slide';
    div.dataset.index = index;
    div.dataset.questionId = question.question_id;

    // API contract: each question has is_multi_select (true | false).
    // - true  → multi-select: user can pick multiple options (checkboxes, "Choose all that apply").
    // - false → single-select: user picks one option only (radio, "Choose one").
    const rawMulti = question.is_multi_select !== undefined ? question.is_multi_select : question.isMultiSelect;
    const isMultiple = (rawMulti === true || rawMulti === 'true' || rawMulti === 1 || rawMulti === '1');

    const isMandatory = question.is_mandatory === true;
    const inputType = isMultiple ? 'checkbox' : 'radio';
    const instructionText = isMultiple ? 'Choose all that apply' : 'Choose one';

    const optionsList = Array.isArray(question.options) ? question.options : [];
    const questionTextLc = String(question.question_text || '').trim().toLowerCase();
    const shouldMoveNoPimpleOptionToEnd = questionTextLc === 'how many red or pus filled pimples do you have now?';
    function isNoneOption(option) {
      if (!option) return false;
      const txt = String(option.option_text || '').trim().toLowerCase();
      const oid = String(option.option_id || '').trim().toLowerCase();
      if (txt === 'none' || txt === 'none of the above') return true;
      if (oid === 'none' || oid === 'no_concern' || oid === 'no_concerns') return true;

      // Treat "No"-style options as exclusive in multi-select concern questions.
      // Covers labels like "No", "No concern", "No acne marks", "No issues", etc.
      if (txt === 'no' || txt.startsWith('no ')) return true;
      if (txt.includes('no concern') || txt.includes('no concerns')) return true;
      if (txt.includes('none')) return true;
      return false;
    }
    function isMoveToEndOption(option) {
      if (!option) return false;
      const txt = String(option.option_text || '').trim().toLowerCase();
      if (isNoneOption(option)) return true;
      // Covers "I don't use...", "I do not use...", "I don't get..." style options.
      if (txt.startsWith("i don't ") || txt.startsWith('i do not ')) return true;
      if (txt.includes("don't use") || txt.includes('do not use')) return true;
      return false;
    }
    const sortedOptionsList = optionsList.slice().sort(function (a, b) {
      const aText = String((a && a.option_text) || '').trim().toLowerCase();
      const bText = String((b && b.option_text) || '').trim().toLowerCase();
      const aIsNoPimple = shouldMoveNoPimpleOptionToEnd && aText === "i don't get red or pus filled pimples";
      const bIsNoPimple = shouldMoveNoPimpleOptionToEnd && bText === "i don't get red or pus filled pimples";
      if (aIsNoPimple && !bIsNoPimple) return 1;
      if (!aIsNoPimple && bIsNoPimple) return -1;

      const aMoveEnd = isMoveToEndOption(a);
      const bMoveEnd = isMoveToEndOption(b);
      if (aMoveEnd && !bMoveEnd) return 1;
      if (!aMoveEnd && bMoveEnd) return -1;
      return 0;
    });
    let optionsHTML = '';
    sortedOptionsList.forEach(function (option, optIndex) {
      optionsHTML += `
        <div class="question-option">
          <input type="${inputType}" name="question_${question.question_id}" id="q_${question.question_id}_opt_${option.option_id}" value="${option.option_id}" data-option-id="${option.option_id}">
          <label for="q_${question.question_id}_opt_${option.option_id}">
            <span class="option-radio ${inputType === 'checkbox' ? 'checkbox' : ''}"></span>
            <span class="option-text">${option.option_text}</span>
          </label>
        </div>
      `;
    });

    let imageHTML = '';
    if (question.image_url) {
      // Handle Shopify asset URLs
      let imageSrc = question.image_url;
      if (!imageSrc.includes('/') && !imageSrc.includes('://')) {
        imageSrc = '/assets/' + imageSrc;
      }
      imageHTML = `<img src="${imageSrc}" alt="Question image" class="question-image" onerror="this.style.display='none';">`;
    }

    div.innerHTML = `
      <div class="question-header">
        <h3>${question.question_text}${isMandatory ? '<span class="mandatory-star">*</span>' : ''}</h3>
        <span class="question-instruction">${instructionText}</span>
      </div>
      ${imageHTML}
      <div class="question-options">${optionsHTML}</div>
    `;

    // Use requestAnimationFrame for better performance
    requestAnimationFrame(function () {
      const inputs = div.querySelectorAll(`input[name="question_${question.question_id}"]`);
      const noneOptionIds = sortedOptionsList.filter(isNoneOption).map(o => String(o.option_id));
      inputs.forEach(function (input) {
        const changeHandler = function () {
          if (isMultiple) {
            const changedId = String(input.dataset.optionId || input.value || '');
            const changedIsNone = noneOptionIds.indexOf(changedId) > -1;

            if (input.checked && changedIsNone) {
              // "None" is exclusive: selecting it unchecks all other options.
              inputs.forEach(function (other) {
                const otherId = String(other.dataset.optionId || other.value || '');
                if (other !== input && other.checked) {
                  other.checked = false;
                  saveAnswer(question.question_id, otherId, true, false);
                }
              });
            } else if (input.checked && !changedIsNone && noneOptionIds.length > 0) {
              // Selecting any real concern should uncheck "None".
              inputs.forEach(function (other) {
                const otherId = String(other.dataset.optionId || other.value || '');
                if (noneOptionIds.indexOf(otherId) > -1 && other.checked) {
                  other.checked = false;
                  saveAnswer(question.question_id, otherId, true, false);
                }
              });
            }
          }

          // For multiple choice, we need to check if at least one is selected to enable next
          if (isMultiple) {
            const anyChecked = Array.from(inputs).some(i => i.checked);
            nextQuestionBtn.disabled = !anyChecked;
          } else {
            nextQuestionBtn.disabled = false;
          }
          saveAnswer(question.question_id, input.dataset.optionId, isMultiple, input.checked);
        };
        input.addEventListener('change', changeHandler);
        // Store handler reference for potential cleanup
        input._changeHandler = changeHandler;
      });
    });

    return div;
  }

  /**
   * Display a specific question
   */
  function showQuestion(index) {
    // Validate index
    if (index < 0 || index >= questionsQueue.length) {
      return;
    }

    const currentQuestion = questionsQueue[index];
    if (!currentQuestion) {
      return;
    }

    // Find slide by data-index attribute (more reliable than array index)
    const slides = document.querySelectorAll('.question-slide');
    let targetSlide = null;

    slides.forEach(function (slide) {
      const slideIndex = parseInt(slide.dataset.index);
      if (slideIndex === index) {
        targetSlide = slide;
        slide.classList.add('active');
      } else {
        slide.classList.remove('active');
      }
    });

    // If slide doesn't exist, create it
    if (!targetSlide && currentQuestion) {
      const slide = createQuestionSlide(currentQuestion, index);
      questionnaireContent.appendChild(slide);
      targetSlide = slide;
      targetSlide.classList.add('active');
    }

    currentQuestionIndex = index;
    updateProgress();

    // Manage Next/Prev buttons
    if (currentQuestionIndex > 0) {
      prevQuestionBtn.style.display = 'block';
      prevQuestionBtn.classList.add('visible');
    } else {
      prevQuestionBtn.style.display = 'none';
      prevQuestionBtn.classList.remove('visible');
    }

    // CRITICAL: Ensure Next button is visible (resetQuestionnaire hides it)
    if (nextQuestionBtn) {
      nextQuestionBtn.style.display = 'block';
      if (currentQuestionIndex === questionsQueue.length - 1) {
        nextQuestionBtn.textContent = 'Complete';
      } else {
        nextQuestionBtn.textContent = 'Next';
      }
    }

    // Start from a clean input state, then restore explicit saved answers.
    const questionInputs = document.querySelectorAll(`input[name="question_${currentQuestion.question_id}"]`);
    questionInputs.forEach(function (input) {
      input.disabled = false;
      input.checked = false;
    });

    // Restore state
    if (currentQuestion) {
      const savedAnswers = questionnaireAnswers.filter(function (a) { return a.question_id === currentQuestion.question_id; });
      if (savedAnswers.length > 0) {
        savedAnswers.forEach(function (ans) {
          const input = document.querySelector(`input[name="question_${currentQuestion.question_id}"][value="${ans.selected_option_id}"]`);
          if (input) input.checked = true;
        });
        nextQuestionBtn.disabled = false;
      } else {
        // For multiple choice, check if any option is selected
        const inputs = document.querySelectorAll(`input[name="question_${currentQuestion.question_id}"]:checked`);
        nextQuestionBtn.disabled = inputs.length === 0;
      }
    }
  }

  /**
   * Save user's answer
   */
  function saveAnswer(questionId, optionId, isMultiple, isChecked) {
    if (isMultiple) {
      if (isChecked) {
        // Add if not exists
        if (!questionnaireAnswers.find(a => a.question_id === questionId && a.selected_option_id === optionId)) {
          questionnaireAnswers.push({
            question_id: questionId,
            selected_option_id: optionId
          });
        }
      } else {
        // Remove
        const index = questionnaireAnswers.findIndex(a => a.question_id === questionId && a.selected_option_id === optionId);
        if (index > -1) {
          questionnaireAnswers.splice(index, 1);
        }
      }
    } else {
      // Single choice: remove all existing for this question and add new
      const existingIndices = [];
      questionnaireAnswers.forEach((a, i) => {
        if (a.question_id === questionId) existingIndices.push(i);
      });

      // Remove from end to start to avoid index shifting
      for (let i = existingIndices.length - 1; i >= 0; i--) {
        questionnaireAnswers.splice(existingIndices[i], 1);
      }

      questionnaireAnswers.push({
        question_id: questionId,
        selected_option_id: optionId
      });
    }

    // Dispatch event or callback if needed
  }

  /**
   * Update progress bar and counter
   */
  function updateProgress() {
    if (!questionsQueue || questionsQueue.length === 0) return;

    const denominator = questionsQueue.length;
    const progress = ((currentQuestionIndex + 1) / denominator) * 100;

    if (progressFill) progressFill.style.width = progress + '%';
    if (currentQuestionSpan) currentQuestionSpan.textContent = currentQuestionIndex + 1;
    if (totalQuestionsSpan) totalQuestionsSpan.textContent = denominator;
  }

  /**
   * Move to next question or complete questionnaire
   */
  function nextQuestion() {
    const currentQuestion = questionsQueue[currentQuestionIndex];
    if (!currentQuestion) return;

    // Get current selections from DOM (for ladder logic)
    const currentInputs = document.querySelectorAll(`input[name="question_${currentQuestion.question_id}"]:checked`);
    const currentSelections = Array.from(currentInputs).map(function (input) {
      return {
        question_id: currentQuestion.question_id,
        selected_option_id: input.dataset.optionId || input.value
      };
    });

    const ladderRules = questionnaireData.order.ladder_rules;
    const orderIndex = currentQuestion.order_index ? currentQuestion.order_index.toString() : "";

    // If this question triggers a ladder
    if (ladderRules && ladderRules[orderIndex]) {
      // ROBUST RESET STRATEGY:
      // 1. Get the list of all "Base Questions" (non-branching questions)
      const baseOrderSet = new Set(questionnaireData.order.base_order.map(s => parseInt(s)));

      // 2. Identify the branch we are about to enter
      const questionsToAdd = [];
      if (currentSelections.length > 0) {
        currentSelections.forEach(function (ans) {
          const newQs = getNextQuestionsFromLadder(currentQuestion, ans.selected_option_id);
          newQs.forEach(q => {
            if (!questionsToAdd.find(x => x.question_id === q.question_id)) {
              questionsToAdd.push(q);
            }
          });
        });
      }

      // 3. CLEANUP: If the user changed their mind, we must remove old answers from the PREVIOUS branch
      // We look at everything in the current queue AFTER this index and remove their answers from DOM
      const futureQuestionIds = questionsQueue.slice(currentQuestionIndex + 1).map(q => q.question_id);
      futureQuestionIds.forEach(function (qId) {
        const futureInputs = document.querySelectorAll(`input[name="question_${qId}"]`);
        futureInputs.forEach(function (input) {
          input.checked = false;
        });
      });

      // CRITICAL: Keep questionnaireAnswers in sync with the DOM.
      // Otherwise old answers from the previous branch can still be submitted on final completion.
      questionnaireAnswers = questionnaireAnswers.filter(function (a) {
        return futureQuestionIds.indexOf(a.question_id) === -1;
      });

      // 4. Reconstruct the Master Queue
      const pastAndCurrent = questionsQueue.slice(0, currentQuestionIndex + 1);
      const futureRaw = questionsQueue.slice(currentQuestionIndex + 1);
      const cleanFuture = futureRaw.filter(q => baseOrderSet.has(q.order_index));

      questionsQueue = [...pastAndCurrent, ...questionsToAdd, ...cleanFuture];

      // 5. HARD SYNC DOM
      const allSlides = document.querySelectorAll('.question-slide');
      allSlides.forEach(slide => {
        if (parseInt(slide.dataset.index) > currentQuestionIndex) {
          slide.remove();
        }
      });

      // Re-render all future slides using DocumentFragment for performance
      const fragment = document.createDocumentFragment();
      for (let i = currentQuestionIndex + 1; i < questionsQueue.length; i++) {
        const slide = createQuestionSlide(questionsQueue[i], i);
        fragment.appendChild(slide);
      }
      // Append immediately (not in requestAnimationFrame) to ensure DOM is ready
      questionnaireContent.appendChild(fragment);

      // Update total count to actual queue length
      totalQuestionsSpan.textContent = questionsQueue.length;

      // Move to next question after DOM is fully updated
      if (currentQuestionIndex + 1 < questionsQueue.length) {
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            showQuestion(currentQuestionIndex + 1);
          });
        });
        return; // Exit early to prevent double execution
      }
    }

    // Move to next question after queue is updated
    if (currentQuestionIndex < questionsQueue.length - 1) {
      showQuestion(currentQuestionIndex + 1);
    } else {
      completeQuestionnaire();
    }
  }

  /**
   * Move to previous question
   */
  function previousQuestion() {
    if (currentQuestionIndex > 0) {
      showQuestion(currentQuestionIndex - 1);
    }
  }

  /**
   * Complete questionnaire and send responses to API
   * POST /tools/process-assessment-and-get-recommendations
   */
  async function completeQuestionnaire() {
    var buttonOriginalText = '';
    try {
      if (nextQuestionBtn) {
        buttonOriginalText = nextQuestionBtn.textContent || 'Complete';
        nextQuestionBtn.disabled = true;
        nextQuestionBtn.textContent = 'Submitting...';
      }

      // Collect all answers from DOM (only when final submit is clicked)
      const allAnswers = [];
      questionsQueue.forEach(function (question) {
        const inputs = document.querySelectorAll(`input[name="question_${question.question_id}"]:checked`);
        inputs.forEach(function (input) {
          allAnswers.push({
            question_id: question.question_id,
            selected_option_id: input.dataset.optionId || input.value
          });
        });
      });

      // Format answers to match API requirements (only question_id and selected_option_id)
      const formattedAnswers = allAnswers.map(function (answer) {
        return {
          question_id: answer.question_id,
          selected_option_id: answer.selected_option_id
        };
      });

      // Determine if user is guest and generate unique guest ID
      const isGuest = !CUSTOMER_ID || CUSTOMER_ID === null || CUSTOMER_ID === 'null' || CUSTOMER_ID === '';
      let userShopifyId;

      if (isGuest) {
        // Generate or retrieve unique guest ID (numeric only to prevent server-side parsing errors)
        try {
          let guestId = localStorage.getItem('chatbot_guest_id');
          // If existing ID is non-numeric, discard it
          if (!guestId || isNaN(guestId.replace('guest_', ''))) {
            // Use timestamp + random digits to create a long numeric ID similar to Shopify
            guestId = Date.now().toString() + Math.floor(Math.random() * 1000).toString();
            localStorage.setItem('chatbot_guest_id', guestId);
          }
          userShopifyId = guestId.replace('guest_', ''); // Ensure no prefix
        } catch (e) {
          userShopifyId = Date.now().toString();
        }
      } else {
        userShopifyId = CUSTOMER_ID.toString();
      }

      // For guest users, use "Guest User" as name, otherwise use actual name
      const userName = (isGuest || !CUSTOMER_NAME || CUSTOMER_NAME.trim() === "")
        ? "Guest User"
        : CUSTOMER_NAME.trim();

      // For guest users, use empty string for email, otherwise use actual email
      const userEmail = (isGuest || !CUSTOMER_EMAIL || CUSTOMER_EMAIL.trim() === "")
        ? ""
        : CUSTOMER_EMAIL.trim();

      const payload = {
        user_shopify_id: String(userShopifyId),
        user_name: String(userName),
        user_email: String(userEmail),
        questionnaire_response: formattedAnswers
      };

      // 0. CLEAR PREVIOUS RESULTS CACHE
      if (window.ChatbotDataManager) {
        window.ChatbotDataManager.setCachedRecommendations(null);
      }
      if (window.ChatbotAPI) {
        window.ChatbotAPI.clearCache('questionnaire_data');
      }

      // 1. SHOW LOADING STATE IN UI
      if (questionnaireContent && typeof window.ChatbotViewController !== 'undefined') {
        if (typeof window.ChatbotViewController.showProgressLoader === 'function') {
          questionnaireContent.innerHTML = window.ChatbotViewController.showProgressLoader('Analyzing your skin profile...');
          if (typeof window.ChatbotViewController.startProgressAnimation === 'function') {
            window.ChatbotViewController.startProgressAnimation(questionnaireContent);
          }
        } else {
          questionnaireContent.innerHTML = window.ChatbotViewController.showSpinner('Preparing your personalized results...', 80);
        }
      }

      console.group('[Questionnaire] Submitting Assessment');
      console.log('Endpoint:', QUESTIONNAIRE_ENDPOINT || 'process-assessment-and-get-recommendations');
      console.log('User ID:', String(userShopifyId));
      console.log('Answer Count:', formattedAnswers.length);
      console.log('Full Payload:', payload);
      console.groupEnd();

      if (!QUESTIONNAIRE_ENDPOINT) {
        console.error('[Questionnaire] QUESTIONNAIRE_ENDPOINT is not set. Check chatbot-main.js passes CONFIG.ENDPOINTS.SUBMIT_RESULTS.');
      }

      let recommendations = null;
      let submitError = null;

      function isTimeoutLikeError(err) {
        if (!err) return false;
        var msg = String((err && err.message) || err).toLowerCase();
        return msg.indexOf('timeout') !== -1 || msg.indexOf('abort') !== -1;
      }

      async function submitViaFetchWithTimeout(url, bodyPayload, timeoutMs) {
        var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
        var timeoutId = controller ? setTimeout(function () { controller.abort(); }, timeoutMs) : null;
        try {
          var response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify(bodyPayload),
            signal: controller ? controller.signal : undefined
          });
          if (timeoutId) clearTimeout(timeoutId);
          if (!response.ok) {
            var errText = await response.text().catch(function () { return ''; });
            throw new Error('API error: ' + response.status + (errText ? (' - ' + errText) : ''));
          }
          return await response.json();
        } catch (e) {
          if (timeoutId) clearTimeout(timeoutId);
          throw e;
        }
      }

      try {
        if (window.ChatbotAPI && typeof window.ChatbotAPI.post === 'function') {
          recommendations = await window.ChatbotAPI.post(QUESTIONNAIRE_ENDPOINT, payload, {
            requireRateLimit: true,
            timeout: 60000,
            retries: 2
          });
          console.log('[Questionnaire] API response received:', recommendations);
        } else {
          recommendations = await submitViaFetchWithTimeout(QUESTIONNAIRE_ENDPOINT, payload, 60000);
          console.log('[Questionnaire] API response keys:', recommendations && typeof recommendations === 'object' ? Object.keys(recommendations) : 'n/a');
        }
      } catch (error) {
        // Root-cause hardening: when primary helper times out, try one direct fetch fallback.
        if (isTimeoutLikeError(error)) {
          try {
            console.warn('[Questionnaire] Primary submit timed out; retrying direct fetch fallback...');
            recommendations = await submitViaFetchWithTimeout(QUESTIONNAIRE_ENDPOINT, payload, 60000);
            console.log('[Questionnaire] Fallback API response received:', recommendations);
          } catch (fallbackErr) {
            submitError = fallbackErr;
          }
        } else {
          submitError = error;
        }
      }

      if (!recommendations) {
        console.error("[Questionnaire] Submission failed:", submitError);
        if (nextQuestionBtn) {
          nextQuestionBtn.disabled = false;
          nextQuestionBtn.textContent = buttonOriginalText || 'Complete';
        }
        if (questionnaireContent) {
          const errDiv = document.createElement('div');
          errDiv.className = 'submission-error-message'; // tag for removal
          errDiv.style.color = '#ef4444';
          errDiv.style.padding = '20px';
          errDiv.style.textAlign = 'center';
          errDiv.innerHTML = `<p>Sorry, we had trouble processing your results.</p>
                              <p style="font-size: 12px; margin-top: 10px;">${(submitError && submitError.message) || 'Please check your connection and try again.'}</p>
                              <button class="welcome-btn primary" style="margin-top: 20px;">Retry</button>`;
          errDiv.querySelector('button').onclick = function () {
            completeQuestionnaire();
          };

          // Only append if we aren't about to navigate away
          // Use innerHTML clearance carefully
          questionnaireContent.innerHTML = '';
          questionnaireContent.appendChild(errDiv);
        }
        return;
      }

      // After successful POST, try to get recommendations from response
      let finalRecommendations = recommendations;

      const hasCategories = finalRecommendations && typeof finalRecommendations === 'object' && !Array.isArray(finalRecommendations) &&
        Object.keys(finalRecommendations).some(k => Array.isArray(finalRecommendations[k]) && !['status', 'msg', 'detail'].includes(k));
      const hasFlatList = finalRecommendations && (Array.isArray(finalRecommendations) || (finalRecommendations.products && finalRecommendations.products.length > 0) || (finalRecommendations.data && Array.isArray(finalRecommendations.data)));

      if (!hasCategories && !hasFlatList) {
        console.log('[Questionnaire] Response missing products, retrying background fetch in 1.5s...');
        // Wait a small moment to let backend sync its DB
        await new Promise(r => setTimeout(r, 1500));

        try {
          if (window.ChatbotConfig && window.ChatbotAPI) {
            const recUrl = `${window.ChatbotConfig.ENDPOINTS.GET_EXISTING_RECOMMENDATIONS}?user_shopify_id=${userShopifyId}`;
            const bgRecs = await window.ChatbotAPI.post(recUrl, {});
            if (bgRecs) {
              finalRecommendations = bgRecs;
              console.log('[Questionnaire] Successfully retrieved results from alternative endpoint.');
            }
          }
        } catch (fetchErr) {
          console.error('[Questionnaire] Alternative results fetch failed:', fetchErr);
        }
      }

      // Transition from simulation to reality
      if (window.ChatbotViewController && typeof window.ChatbotViewController.completeProgressAnimation === 'function') {
        const questionnaireContent = document.getElementById('questionnaire-content');
        window.ChatbotViewController.completeProgressAnimation(questionnaireContent, () => {
          if (typeof showChatInterface === 'function') {
            showChatInterface(finalRecommendations, { openSkinReportOnLoad: true });
          }
        });
      } else if (typeof showChatInterface === 'function') {
        // Fallback if VC logic missing
        showChatInterface(finalRecommendations, { openSkinReportOnLoad: true });
      } else {
        console.error('[Questionnaire] showChatInterface not injected. Results will not display.');
        if (nextQuestionBtn) {
          nextQuestionBtn.disabled = false;
          nextQuestionBtn.textContent = buttonOriginalText || 'Complete';
        }
        if (questionnaireContent) {
          questionnaireContent.innerHTML = '<div style="text-align:center; padding: 40px;"><p style="color:#ef4444;">Error: Chat interface not available.</p></div>';
        }
      }
    } catch (error) {
      console.error('[Questionnaire] completeQuestionnaire error:', error);
      if (nextQuestionBtn) {
        nextQuestionBtn.disabled = false;
        nextQuestionBtn.textContent = buttonOriginalText || 'Complete';
      }
      if (typeof showChatInterface === 'function') {
        showChatInterface(null, { openSkinReportOnLoad: true });
      }
    }
  }

  /**
   * Reset questionnaire state
   */
  function resetQuestionnaire() {
    questionsQueue = [];
    questionsMap = {};
    questionnaireAnswers = [];
    currentQuestionIndex = 0;
    maxTotalQuestions = 0;

    // Clear UI state
    if (questionnaireContent) {
      questionnaireContent.innerHTML = '';
    }

    // Hide navigation buttons
    if (nextQuestionBtn) {
      nextQuestionBtn.style.display = 'none';
      nextQuestionBtn.textContent = 'Next';
      nextQuestionBtn.disabled = true;
    }

    if (prevQuestionBtn) {
      prevQuestionBtn.style.display = 'none';
      prevQuestionBtn.classList.remove('visible');
    }

    // Reset progress
    if (progressFill) {
      progressFill.style.width = '0%';
    }
    if (currentQuestionSpan) {
      currentQuestionSpan.textContent = '0';
    }
    if (totalQuestionsSpan) {
      totalQuestionsSpan.textContent = '0';
    }
  }

  // Public API
  return {
    setDOMReferences: setDOMReferences,
    initializeQuestionnaire: initializeQuestionnaire,
    nextQuestion: nextQuestion,
    previousQuestion: previousQuestion,
    resetQuestionnaire: resetQuestionnaire,
    setAnswers: function (answers) {
      questionnaireAnswers = Array.isArray(answers) ? answers : [];
      // Re-build map and queue if necessary? No, initializeQuestionnaire does that based on answers
    },
    getAnswers: function () { return questionnaireAnswers; },
    getQuestionsQueue: function () { return questionsQueue; }
  };
}

// Create global instance
const ChatbotQuestionnaire = initChatbotQuestionnaire();
